import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";

import i18n from "i18n";
import useRequest from "hooks/useRequest";
import useFilters from "hooks/useFilters";
import { isMobile } from "helpers/Mobile";
import { parseDateToServer } from "helpers/utils/dates";
import { getUrl } from "helpers/orders/OrderHelper";

import { BiService } from "services/BiService";
import { OrderService } from "services/OrderService";

import AdminLayout from "components/MainApp/layouts/DesktopLayout";
import MostSelledCard from "components/MainApp/organisms/Dashboard/MostSelledCard";
import OrderStatesCard from "components/MainApp/organisms/Dashboard/OrderStatesCard";
import DeliveredOrdersCard from "components/MainApp/organisms/Dashboard/DeliveredOrdersCard";
import MonthlySalesCard from "components/MainApp/organisms/Dashboard/MonthlySalesCard";
import DashboardTable from "components/MainApp/organisms/Dashboard/Table";
import DropdownOptions from "components/MainApp/organisms/Dashboard/DropdownOptions";

import styles from "./Dashboard.module.scss";
import Loader from "components/MainApp/atoms/Loader";
import { AuthService } from "services/AuthService";

const colorsOrdersStatus = ["#62D862", "#3C7AF5", "#FFAF3A", "#F54B5A"];

const tableHeaders = {
  type: {
    name: "type",
    title: i18n.t("type").toUpperCase(),
    active: false,
    direction: false,
    value: "type",
  },
  code: {
    name: "code",
    title: i18n.t("code").toUpperCase(),
    active: false,
    direction: false,
    value: "code",
  },
  date: {
    name: "date",
    title: i18n.t("creation.date").toUpperCase(),
    active: false,
    direction: false,
    value: "created_at",
  },
  owner: {
    name: "owner",
    title: i18n.t("owner").toUpperCase(),
    active: false,
    direction: false,
    value: "owner__name",
  },
  state: {
    name: "state",
    title: i18n.t("state").toUpperCase(),
    active: false,
    direction: false,
    value: "status",
  },
};

const initialFilters = {
  // page_size: 5,
  ordering: "-created_at",
  from_date: null,
};

const options = [
  {
    key: "1",
    value: i18n.t("last.24.hours"),
  },
  {
    key: "7",
    value: i18n.t("last.7.days"),
  },
  {
    key: "30",
    value: i18n.t("last.30.days"),
  },
];

const Dashboard = () => {
  const history = useHistory();
  const {
    loading,
    beforeSubmit,
    afterSubmit,
   } = useRequest();

  const [data, setData] = useState(null);

  const [itemsSell, setItemsSell] = useState([]);
  const [clientsRanking, setClientsRanking] = useState([]);

  const [ordersStatus, setOrdersStatus] = useState([]);
  const [ordersStatusFilter, setOrdersStatusFilter] = useState([]);
  const [totalOrdersStatus, setTotalOrdersStatus] = useState(0);

  const [salesByMonthX, setSalesByMonthX] = useState([]);
  const [salesByMonthV, setSalesByMonthV] = useState([]);
  const [loading2, setLoading2] = useState(false);

  const [
    filters,
    setFilters,
    selectedFiltersAll,
    selectedFiltersByType,
    handleApplyFilters,
    handleSearch,
    headerOrderingHandler,
    resetOrdering,
    handleFiltersChangeOrdering,
    applied,
    page,
    setPage,
    items,
    setItems,
    pager,
    setPager,
    header,
    setHeader,
    mobilePanelOpen,
    setMobilePanelOpen,
  ] = useFilters(initialFilters, [], [], isMobile, tableHeaders);

  useEffect(() => {

    setLoading2(true);
    BiService.dashboard().then((data) => {
      setData(data);

      mapItemsSell(data["SALE_PRODUCTS"]["payload"]);
      mapOrdersStatus(data["ORDERS_STATUS"]["payload"]);
      mapClientsRanking(data["CLIENT_RANKING"]["payload"]);
      mapSalesByMonth(data["SALES_MONTH"]["payload"]);
      setLoading2(false);
    })
    .catch((e) => {
      setLoading2(false);
    });

  }, []);

  useEffect(() => {
    if (filters["from_date"] == null) {
      setFromDateFilter(1);
    } else {
      filter(false);
    }
  }, [filters]);

  const setFromDateFilter = (days) => {
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - days);
    const newFilters = { ...filters };
    newFilters["page_size"] = 600;
    newFilters["from_date"] = parseDateToServer(fromDate, false, false);
    newFilters["node"] = AuthService.getCurrentNodeCode();
    setFilters(newFilters);
  };

  const filter = () => {
    const data = { ...filters };
    beforeSubmit();
    OrderService.orders(data)
      .then((response) => {
        afterSubmit();
        setItems(response.results);
      })
      .catch((e) => {
        afterSubmit();
      });
  };

  const handleChangeFilterItemsSell = (key) => {
    const key_to_field = {
      name: "item_val_name",
      sku: "item_val_sku",
    };
    mapItemsSell(data["SALE_PRODUCTS"]["payload"], key_to_field[key]);
  };

  const mapItemsSell = (items, filed_label = "item_val_name") => {
    let total = 0;
    items.forEach((i) => {
      total += i.total;
    });
    items = items.map((i) => {
      return {
        product: i[filed_label],
        selled: i.total,
        selledPercentage: (i.total * 100) / total,
      };
    });
    setItemsSell(items);
  };

  const mapClientsRanking = (items) => {
    items = items.map((c) => {
      return {
        client: c.client__name,
        orders: c.total,
      };
    });
    setClientsRanking(items);
  };

  const handleChangeFilterOrdersStatus = (type) => {
    mapOrdersStatus(data["ORDERS_STATUS"]["payload"], type);
  };

  const mapOrdersStatus = (items, type = "all") => {
    const series = { COM: 0, PRO: 0, PEN: 0, REJ: 0};
    let total = 0;

    items
      .filter((i) => {
        return type === "all" || i.type === type;
      })
      .forEach((i) => {
        if (i.status in series) {
          series[i.status] += i.total;
          total += i.total;
        }
      });

    setOrdersStatusFilter(
      Object.keys(series).map((key) => {
        return [i18n.t(`orders.status.${key}`), series[key]];
      })
    );
    setTotalOrdersStatus(total);
  };

  const mapSalesByMonth = (items) => {
    setSalesByMonthX(items.map((s) => i18n.t(`months.numeric.${s.month}`)));

    const values = [
      {
        name: i18n.t("sales"),
      },
    ];
    values[0]["data"] = items.map((s) => s.total);
    setSalesByMonthV(values);
  };

  const goToDetailPage = (event) => {
    let url = getUrl(
      event.currentTarget.dataset.requestorderId,
      event.currentTarget.dataset.requestorderType
    );
    if (url) {
      history.push(url);
    }
  };

  const handleChangeDate = (key) => {
    setFromDateFilter(parseInt(key));
  };

  const content = (
    <div className={styles["dashboard-wrapper"]}>
      <div className={styles["dashboard-wrapper-inner"]}>
        <div className={styles["dashboard-charts"]}>
          <div className={styles["dashboard-charts-first-row"]}>
            <div className={styles["dashboard-charts-most-selled"]}>
              <MostSelledCard
                items={itemsSell}
                handleChangeFilter={handleChangeFilterItemsSell}
              />
            </div>
            <div className={styles["dashboard-charts-order-states"]}>
              <OrderStatesCard
                series={ordersStatusFilter}
                colors={colorsOrdersStatus}
                totalNumber={totalOrdersStatus}
                handleChangeFilter={handleChangeFilterOrdersStatus}
              />
            </div>
          </div>
          <div className={styles["dashboard-charts-second-row"]}>
            <div className={styles["dashboard-charts-delivered"]}>
              <DeliveredOrdersCard items={clientsRanking} />
            </div>
            <div className={styles["dashboard-charts-monthly-sales"]}>
              <MonthlySalesCard
                series={salesByMonthV}
                xCategories={salesByMonthX}
              />
            </div>
          </div>
        </div>
      </div>
      <div className={styles["dashboard-table-wrapper"]}>
        <div className={styles["dashboard-table-header"]}>
          <h1 className={styles["dashboard-table-title"]}>{i18n.t("last.orders")}</h1>
          <DropdownOptions
            options={options}
            selectedOption={"1"}
            handleChange={setFromDateFilter}
          />
        </div>
        <DashboardTable
          orders={items}
          headers={tableHeaders}
          headerClickOrder={headerOrderingHandler}
          handleClickOrder={goToDetailPage}
        />
      </div>
    </div>
  );

  return (
    <div className={styles["dashboard-container"]}>
      <AdminLayout
        headerTitle={i18n.t("start")}
        headerOptions={[]}
        content={content}
        navHidden={false}
      ></AdminLayout>
      {(loading || loading2) && <Loader />}
    </div>
  );
};

export default Dashboard;
